import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';

import { StateController } from '../../../helpers/state/state-management/state-controller';
import { TranslationElement } from '../../../types';
import { createTopicRequestFromTopic } from '../../../helpers/api/topics-api';
import featuresInfo from '../../../helpers/state/features-info';
import { getMainPage } from '../../../helpers/page-name';
import { isDefined } from '../../../helpers/utils';
import { openChatWindow } from '../../../helpers/intercom';
import organizationInfo from '../../../helpers/state/organization-info';
import { supportedLanguages } from '../../../helpers/constants';
import topicData from '../../../helpers/state/topic-data';
import translationData from '../../../helpers/state/translation-data';

@customElement('admin-translation-addon')
export class AdminTranslationAddon extends LitElement {
    amplitude_callsite: string = 'Translation Settings Drawer';

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindTranslationState = new StateController(this, translationData);
    private readonly bindAccountState = new StateController(this, translationData);

    @query('#translated-text')
    private readonly translatedTextElement?: HTMLInputElement;

    @state()
    newPhraseLanguage?: string;

    @state()
    newPhraseTranslated?: string;

    @state()
    newPhraseEnglish?: string;

    @state()
    showCustomTranslation: boolean = false;

    @state()
    selectedLanguage?: string = 'en';

    @state()
    selectedLanguageName: string = '';

    @state()
    showAddLanguage: boolean = false;

    @state()
    searchResults: string[] = [];

    @state()
    selectedPhrase?: string;

    @state()
    selectedPhraseOriginalTranslation?: string;

    @state()
    english_phrases: string[] = [];

    openExtension() {
        window.open('https://chrome.google.com/webstore/detail/proxi/kambmamfjckgbcneopcmbemlmikcnalb', '_blank');
    }

    private async addLanguage(e: Event) {
        const shorthand = (e.target as HTMLInputElement).value;
        // if the language is already in the list, don't add it again
        if (topicData.topic?.translation_languages?.map((lang) => lang.language.toLowerCase()).includes(shorthand.toLowerCase()) === true) {
            getMainPage().errorToastElement.showToastAndThrow('Language already added');
            return;
        }
        const updatedTopic = createTopicRequestFromTopic(topicData.topic);
        if (!updatedTopic.translation_languages) {
            updatedTopic.translation_languages = [];
        }
        updatedTopic.translation_languages.push(
            {
                language: shorthand,
                icon: undefined,
            }
        );
        try {
            await topicData.fetchAndUpdateTopicData(updatedTopic, this.amplitude_callsite);
        } catch (error) {
            getMainPage().errorToastElement.showToastAndThrow('Error adding language');
        }
        this.toggleShowAddLanguage(false);
        this.requestUpdate();
    }

    private async setDefaultLanguage(e: Event) {
        const code = (e.target as HTMLSelectElement).value;
        const current = topicData.topic?.default_language ?? 'auto';
        if (!code || code === current) {
            return;
        }
        const updatedTopic = createTopicRequestFromTopic(topicData.topic);
        updatedTopic.default_language = code === 'auto' ? undefined : code;
        try {
            await topicData.fetchAndUpdateTopicData(updatedTopic, this.amplitude_callsite);
        } catch (error) {
            getMainPage().errorToastElement.showToastAndThrow('Error saving the default language');
        }
    }

    private handleSearchInput(event: Event) {
        const searchBar = event.target as HTMLInputElement;
        const searchText = searchBar.value.toLowerCase();
        this.searchResults = this.english_phrases.filter((phrase) => phrase.toLowerCase().includes(searchText));
    }

    private async selectPhrase(phrase: string) {
        this.selectedPhrase = phrase;
        this.selectedPhraseOriginalTranslation = await translationData.translatePhrase(phrase);
    }

    private async addCustomTranslation() {
        if (organizationInfo.id === undefined || organizationInfo.id === null) {
            console.error('Organization ID not found');
            return;
        }
        this.newPhraseEnglish = this.selectedPhrase;
        this.newPhraseTranslated = this.translatedTextElement?.value;
        this.newPhraseLanguage = this.selectedLanguage;
        if (!this.newPhraseLanguage || !this.newPhraseTranslated || !this.newPhraseEnglish) {
            console.error('Missing fields');
            getMainPage().errorToastElement.showToastAndThrow('Please fill out all fields');
            return;
        }
        const translation: TranslationElement = {
            original_text: this.newPhraseEnglish,
            translated_text: this.newPhraseTranslated,
            language: this.newPhraseLanguage,
        };
        await translationData.addCustomTranslation(translation, this.amplitude_callsite);
        this.showCustomTranslation = false;
        this.selectedPhrase = undefined;
        this.selectedPhraseOriginalTranslation = undefined;
        this.requestUpdate();
    }

    private async selectLanguage(language: string) {
        this.toggleShowAddLanguage(false);
        this.selectedLanguage = language;
        await translationData.changeLanguage('en', language);
        this.selectedLanguageName = supportedLanguages.find((lang) => lang.code === language)?.name ?? '';
        this.requestUpdate();

        const english_phrases_auto = translationData.allTranslations.map((translation) => translation.original_text).flat();
        const english_phrases_custom = translationData.allTranslationSets.map((translation) => translation.translations.map((t) => t.original_text)).flat();
        const all_phrases = english_phrases_auto.concat(english_phrases_custom);
        this.english_phrases = [...new Set(all_phrases)];

        if (this.selectedPhrase) {
            this.selectedPhraseOriginalTranslation = await translationData.translatePhrase(this.selectedPhrase);
            this.requestUpdate();
        }
    }

    toggleLiveEditMode() {
        translationData.liveEditMode = !translationData.liveEditMode;
        this.requestUpdate();
    }

    toggleShowAddLanguage(state: boolean) {
        this.showAddLanguage = state;
        this.requestUpdate();
    }

    render() {
        const topic_languages = topicData.topic?.translation_languages?.map((lang) => lang.language) ?? [];
        // check that languages are supported and get native name
        const final_languages = supportedLanguages
            .filter((language: { code: string; }) => topic_languages.includes(language.code))
            .map(language => ({ ...language, nativeName: language.nativeName }));

        // if english isn't in the list, add it to the front
        if (!final_languages.some((language: { code: string; }) => language.code === 'en')) {
            final_languages.unshift({ code: 'en', name: 'English', nativeName: 'English' });
        }

        return html`
            <div class="addon__container">
                ${featuresInfo.translationLanguagesEnabled > 0 ? html`
                    <h4 class="info-paragraph horizontal sub-title" style="margin-top: 10px">
                        <span style="margin-right:10px">Turn on Live Translation Editing</span>
                        <form-switch @change="${this.toggleLiveEditMode}" ?checked="${translationData.liveEditMode}"></form-switch>
                    </h4>
                    <h4 class="sub-title">Your Current Languages:</h4>
                    <div class="pill-container">
                        ${final_languages.map((language) => html`
                            <span class="pill ${language.code === this.selectedLanguage ? 'selected' : ''}"
                                @click=${async() => await this.selectLanguage(language.code)}>${language.name} (${language.nativeName})</span>
                        `)}
                        ${(topicData.topic?.translation_languages?.length ?? 0) < (featuresInfo.translationLanguagesEnabled + 1) ? html`
                            <span class="pill add-new" @click=${() => this.toggleShowAddLanguage(true)}>
                                <span class="horizontal">
                                    <mwc-icon>add</mwc-icon>
                                    Add Language
                                </span>
                            </span>
                        ` : html`
                            <br>
                            <span @click="${() => openChatWindow(this.amplitude_callsite, `I would like to add another tranlsation language to my account`)}" style="text-decoration:underline; cursor:pointer">Chat with Us To Enable More Languages</span>
                        `}
                    </div>
                    ${final_languages.length > 1 ? html`
                        <h4 class="sub-title">Language Visitors Start In:</h4>
                        <mwc-select outlined @selected="${this.setDefaultLanguage}">
                            <mwc-list-item value="auto" ?selected="${!isDefined(topicData.topic?.default_language)}">Automatic (match the visitor's phone)</mwc-list-item>
                            ${final_languages.map((language) => html`
                                <mwc-list-item value="${language.code}" ?selected="${topicData.topic?.default_language === language.code}">${language.name} (${language.nativeName})</mwc-list-item>
                            `)}
                        </mwc-select>
                        <p class="info-paragraph">Automatic picks the visitor's phone language when it's one of yours, otherwise English. Choosing a language starts everyone there. Either way, we remember what a visitor picks in the switcher.</p>
                    ` : ''}
                    <div class="info-container">
                        ${this.renderLanguageSection()}
                    </div>
                ` : html`
                    <div class="info-container">
                        <p class="info-paragraph">
                            Reach out to Proxi to enable auto-translation for your account. The auto-translation add-on starts at $50/month/language
                            <br>
                            <span @click="${() => openChatWindow(this.amplitude_callsite, `I am interested in Proxi learning more about Proxi auto-translation`)}" style="text-decoration:underline; cursor:pointer">Chat with Us</span>
                        </p>
                    </div>
                `}
            </div>
            <error-toast id="errorToast"></error-toast>
		`;
    }

    renderLanguageSection() {
        if (this.showAddLanguage) {
            return html`
                <mwc-select outlined label="Add a Language" @selected="${this.addLanguage}">
                    ${supportedLanguages.filter(lang => lang.code !== 'en' &&
                        !((topicData.topic?.translation_languages ?? []).map((lang) => lang.language).includes(lang.code))
                    ).map((language) => html`
                        <mwc-list-item value="${language.code}">${language.name}</mwc-list-item>`)}
                </mwc-select>
            `;
        } else if (this.selectedLanguage !== 'en' && this.selectedLanguage !== '') {

            return html`
                ${!this.selectedPhrase ? html`
                    <span class="sub-title">Edit ${this.selectedLanguageName} Translation Overrides:</span><br>
                    <proxi-textfield id="search-bar" label="Search for an English Phrase" style="width:100%" @input=${this.handleSearchInput} outlined></proxi-textfield>
                    <div id="pill-container">
                        ${this.searchResults.map((phrase) => html`
                            <li class="pill phrase" @click=${async () => await this.selectPhrase(phrase)}>${phrase}</li>
                        `)}
                    </div>
                ` : html`
                    <span class="sub-title">Edit ${this.selectedLanguageName} Translation for: <br><b>${this.selectedPhrase}</b></span>
                    <br>
                    <mwc-textarea id="translated-text" label="${this.selectedLanguageName} Translation" value=${this.selectedPhraseOriginalTranslation ?? ''} outlined></mwc-textarea>
                    <div class="horizontal addon__footer">
                        <form-button size="md" type="submit" variant="outline" @click="${() => { this.selectedPhrase = undefined; this.selectedPhraseOriginalTranslation = undefined; }}">Cancel</form-button>
                        <form-button size="md" type="submit" @click="${this.addCustomTranslation}">Save Translation</form-button>
                    </div>
                `}
            `;
        } else {
            return html`
            `;
        }

    }

    static get styles() {
        return css`
            .addon__container {
                flex-direction: column;
                max-height: calc(100vh - 90px);
                height:100%;
                overflow: hidden auto;
                margin-bottom: 1rem;
                padding-right: 30px;
            }

            /* width */
            .addon__container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .addon__container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .addon__container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .drawer_image {
                margin-top: 1rem;
                margin-bottom: 1rem;
            }

            .pill-container {
                display: flex;
                flex-wrap: wrap;
                gap: 10px;
            }

            .pill {
                display: inline-block;
                padding: 5px 10px;
                border-radius: 20px;
                background-color: var(--main-primary-color);
                color: white;
                cursor: pointer;
            }

            .pill.add-new {
                background-color: white;
                color: var(--main-primary-color);
                border: 1px solid var(--main-primary-color);
            }

            mwc-icon {
                margin-right: 5px;
                --mdc-icon-size: 16px;
            }

            proxi-textfield {
                margin-top: 20px;
                margin-bottom: 20px;
            }

            .pill.selected {
                background-color: var(--main-secondary-color);
            }

            .info-container {
                margin-top: 10px;
                background-color: var(--theme-lightest-color);
                padding: 10px;
            }

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .info-paragraph {
                color: var(--theme-light-text-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .horizontal {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                align-items: center;
            }

            .sub-title {
                margin-bottom: 5px;
                color: var(--theme-light-text-color);
            }

            .pill.phrase {
                display: inline-block;
                max-width: 150px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                cursor: pointer;
            }

            #translated-text {
                margin-top: 20px;
                margin-bottom: 20px;
                width: 100%;
            }

            .addon__footer {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                align-items: center;
                padding: 0 20px;
                gap: 10px;
            }
        `;
    }
}